/**
 * navigation.config.js — single source of truth for ApexFlow navigation.
 *
 * Each item:
 *   • id           : stable key (used for testids)
 *   • to           : route path
 *   • label        : display label
 *   • Icon         : lucide-react icon component
 *   • roles        : array of allowed roles; empty = everyone authenticated
 *   • placement    : where to render. Combinations allowed.
 *       - 'desktop-primary' : inline on desktop sticky navbar (>=768px)
 *       - 'mobile-bottom'   : appears in 5-tab bottom nav (<640px)
 *       - 'more'            : appears in MORE drawer (mobile) and MORE
 *                             dropdown (desktop)
 *
 * Existing test-IDs preserved where possible (`nav-strategies`,
 * `nav-portfolio`, `nav-banking`, `nav-admin`, `mobile-tab-*`,
 * `nav-overflow-*`).
 */
import {
  Home, Briefcase, Workflow, Network, Wallet, UserCog,
  ShieldCheck, Settings as SettingsIcon, Store,
  History, BarChart3, FileText, Download, ShieldAlert,
  Banknote, Cpu, Rocket, Shield, Activity, Archive, Lock,
  Edit3, Trophy, Gavel,
} from 'lucide-react';

// All roles that exist in the codebase today
export const ROLES = { ADMIN: 'admin', OPERATOR: 'operator' };

const ALL = []; // empty roles array → visible to everyone authenticated
const ADMIN_ONLY = [ROLES.ADMIN];

export const NAV_ITEMS = [
  // Spec mobile-bottom 5: Dashboard · Portfolio · Analytics · Reports · More
  { id: 'dashboard',  to: '/',          label: 'DASHBOARD',
    Icon: Home,        roles: ALL,
    placement: ['desktop-primary', 'mobile-bottom'],
    testId: 'nav-dashboard',     mobileTestId: 'mobile-tab-home' },

  { id: 'portfolio',  to: '/portfolio', label: 'PORTFOLIO',
    Icon: Briefcase,   roles: ALL,
    placement: ['desktop-primary', 'mobile-bottom'],
    testId: 'nav-portfolio',     mobileTestId: 'mobile-tab-portfolio' },

  { id: 'analytics',  to: '/analytics', label: 'ANALYTICS',
    Icon: BarChart3,   roles: ALL,
    placement: ['desktop-primary', 'mobile-bottom'],
    testId: 'nav-analytics',     mobileTestId: 'mobile-tab-analytics' },

  { id: 'reports',    to: '/reports',   label: 'REPORTS',
    Icon: FileText,    roles: ALL,
    placement: ['desktop-primary', 'mobile-bottom'],
    testId: 'nav-reports',       mobileTestId: 'mobile-tab-reports' },

  // Items that live in MORE (both desktop dropdown + mobile drawer).
  // Spec rationale: keep desktop primary minimal (4-5 items) so the
  // sticky navbar doesn't wrap or clip on tablets/laptops.
  { id: 'strategies', to: '/strategies', label: 'STRATEGIES',
    Icon: Workflow,    roles: ALL,
    placement: ['more'],
    testId: 'nav-strategies' },

  { id: 'venues',     to: '/venues',    label: 'VENUES',
    Icon: Network,     roles: ALL,
    placement: ['more'],
    testId: 'nav-venues' },

  { id: 'banking',    to: '/banking',   label: 'BANKING',
    Icon: Wallet,      roles: ALL,
    placement: ['more'],
    testId: 'nav-banking' },

  // MORE-only items
  { id: 'profit-engine', to: '/profit-engine', label: 'PROFIT ENGINE',
    Icon: Cpu,         roles: ALL,
    placement: ['more'],
    testId: 'nav-profit-engine' },

  { id: 'trading-history', to: '/trading-history', label: 'TRADING HISTORY',
    Icon: History,     roles: ALL,
    placement: ['more'],
    testId: 'nav-trading-history' },

  { id: 'risk',       to: '/risk',      label: 'RISK',
    Icon: ShieldAlert, roles: ALL,
    placement: ['more'],
    testId: 'nav-risk' },

  { id: 'payouts',    to: '/payouts',   label: 'PAYOUTS',
    Icon: Banknote,    roles: ALL,
    placement: ['more'],
    testId: 'nav-payouts' },

  { id: 'csv-downloads', to: '/csv-downloads', label: 'CSV DOWNLOADS',
    Icon: Download,    roles: ALL,
    placement: ['more'],
    testId: 'nav-csv-downloads' },

  { id: 'marketplace', to: '/marketplace', label: 'MARKETPLACE',
    Icon: Store,       roles: ALL,
    placement: ['more'],
    testId: 'nav-marketplace' },

  // Admin / Compliance — role-gated
  { id: 'admin',      to: '/admin',     label: 'ADMIN',
    Icon: UserCog,     roles: ADMIN_ONLY,
    placement: ['desktop-primary', 'more'],
    testId: 'nav-admin' },

  { id: 'compliance', to: '/compliance', label: 'COMPLIANCE',
    Icon: ShieldCheck, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-compliance' },
  { id: 'promotion-console', to: '/promotion-console',
    label: 'PROMOTION CONSOLE',
    Icon: Rocket, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-promotion-console' },
  { id: 'exchange-health', to: '/exchange-health',
    label: 'EXCHANGE HEALTH',
    Icon: Network, roles: ALL,
    placement: ['more'],
    testId: 'nav-exchange-health' },
  { id: 'capital-preservation', to: '/capital-preservation',
    label: 'CAPITAL PRESERVATION',
    Icon: Shield, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-capital-preservation' },
  { id: 'executive-brief', to: '/executive-brief',
    label: 'EXECUTIVE BRIEF',
    Icon: FileText, roles: ALL,
    placement: ['more'],
    testId: 'nav-executive-brief' },
  { id: 'briefing-history', to: '/executive-brief/history',
    label: 'BRIEFING HISTORY',
    Icon: FileText, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-briefing-history' },
  { id: 'soak-monitor', to: '/soak-monitor',
    label: 'SOAK MONITOR',
    Icon: Activity, roles: ALL,
    placement: ['more'],
    testId: 'nav-soak-monitor' },
  { id: 'phase5-dashboard', to: '/phase5-dashboard',
    label: 'PHASE 5 DASHBOARD',
    Icon: Workflow, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-phase5-dashboard' },
  { id: 'profitability', to: '/profitability',
    label: 'PROFITABILITY',
    Icon: Trophy, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-profitability' },
  { id: 'compliance-vault', to: '/compliance-vault',
    label: 'COMPLIANCE VAULT',
    Icon: Archive, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-compliance-vault' },
  { id: 'bayesian-gate-tracker', to: '/bayesian-gate-tracker',
    label: 'BAYESIAN GATE',
    Icon: Lock, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-bayesian-gate-tracker' },
  { id: 'auditor-signing-ceremony',
    to: '/auditor-signing-ceremony',
    label: 'AUDITOR SIGNING',
    Icon: Edit3, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-auditor-signing-ceremony' },
  { id: 'governance', to: '/governance',
    label: 'GOVERNANCE',
    Icon: Gavel, roles: ADMIN_ONLY,
    placement: ['more'],
    testId: 'nav-governance' },
  { id: 'settings',   to: '/settings',  label: 'SETTINGS',
    Icon: SettingsIcon, roles: ALL,
    placement: ['more'],
    testId: 'nav-settings' },
];

/**
 * Filter items by role + placement.
 *   placement: 'desktop-primary' | 'mobile-bottom' | 'more'
 */
export function visibleItems(role, placement) {
  return NAV_ITEMS.filter((it) => {
    if (it.placement.indexOf(placement) === -1) return false;
    if (it.roles.length === 0) return true;
    return it.roles.indexOf(role) !== -1;
  });
}
